/* import __COLOCATED_TEMPLATE__ from './suggestion-editor.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import { action } from '@ember/object';
import { next, schedule } from '@ember/runloop';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type ComposerPublicAPI } from '@intercom/embercom-prosemirror-composer';
import type Composer from '@intercom/embercom-prosemirror-composer/lib/composer';
import type { Block } from '@intercom/interblocks.ts';
import type ContentReviewRequest from 'embercom/models/content-service/content-review-request';
import { EntityType } from 'embercom/models/data/entity-types';
import type SuggestionService from 'embercom/services/suggestion-service';
import { ReviewType } from 'embercom/models/content-service/content-review-request';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';
import { type ReviewableContent } from 'embercom/components/knowledge-hub/content-editor/content-review';
import { timeout } from 'ember-concurrency';
import ENV from 'embercom/config/environment';

interface SuggestionEditorArgs {
  reviewRequests: ContentReviewRequest[];
  activeContent: ReviewableContent;
  isDetailsSidePanelOpen: boolean;
  generationInProgress: boolean;
}

export type BlockGroup = {
  id: string;
  isEdited: boolean;
  blocksBefore?: Block[];
  blocks: Block[];
  reviewRequest?: ContentReviewRequest;
  composerApi?: ComposerPublicAPI;
};

type SuggestionCardLayoutDetails = {
  blockGroupId: string;
  cardId: string;
  idealTop: number;
  cardElement: HTMLElement;
  cardHeight: number;
};

export const SPACING_BETWEEN_CARDS = 16;

export default class SuggestionEditor extends Component<SuggestionEditorArgs> {
  @service declare appService: $TSFixMe;
  @service declare suggestionService: SuggestionService;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;

  @tracked showPreviousVersion = false;
  @tracked declare updatedBlocksForIndex: Block[];
  @tracked declare focusedComposer?: Composer;
  @tracked declare focusedBlockGroup?: BlockGroup;
  @tracked moveContentModalOpen = false;

  constructor(owner: any, args: SuggestionEditorArgs) {
    super(owner, args);
    this.suggestionService.createBlockGroups(this.args.activeContent, this.args.reviewRequests);
    schedule('afterRender', () => {
      next(() => {
        this.scrollToFirstEditedBlockGroup();
        this.focusOnBlockGroupSuggestion();
      });
    });
  }

  get isArticleContent() {
    return this.args.activeContent.entityType === EntityType.ArticleContent;
  }

  get isContentSnippet() {
    return this.args.activeContent.entityType === EntityType.ContentSnippet;
  }

  get isCreateContent() {
    //create_content requests should only ever have one request in the array
    return this.args.reviewRequests.every(
      (request) => request.get('reviewType') === ReviewType.CREATE_CONTENT,
    );
  }

  get blockGroups(): BlockGroup[] {
    return this.suggestionService.blockGroups;
  }

  get canUseItemsCenterForWrapper() {
    return (
      this.knowledgeHubDrawerEditorService.isFullScreen ||
      !this.args.isDetailsSidePanelOpen ||
      this.args.generationInProgress
    );
  }

  get hasPendingSuggestions() {
    return this.suggestionService.numberOfPendingSuggestions > 0;
  }

  @action
  scrollToFirstEditedBlockGroup() {
    if (this.editedBlockGroups.length > 0) {
      this.scrollToBlockGroup(this.editedBlockGroups[0]);
    }
  }

  scrollToBlockGroup(blockGroup: BlockGroup) {
    let blockGroupElement = document.getElementById(blockGroup.id);
    if (blockGroupElement) {
      blockGroupElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  get editedBlockGroups(): BlockGroup[] {
    return this.blockGroups.filter((group) => group.isEdited);
  }

  get uneditedBlockGroups(): BlockGroup[] {
    return this.blockGroups.filter((group) => !group.isEdited);
  }

  get suggestionsToEditBlockGroups(): BlockGroup[] {
    return this.blockGroups;
  }

  get pendingEditedBlockGroups(): BlockGroup[] {
    return this.editedBlockGroups.filter(
      (group) => group.reviewRequest?.get('status') === Status.Pending,
    );
  }

  @action
  toggleShowPreviousVersion() {
    this.showPreviousVersion = !this.showPreviousVersion;
  }

  @action
  setFocusedComposer(composer?: Composer) {
    this.focusedComposer = composer;
  }

  @action
  shouldShowSuggestionCard(blockGroup: BlockGroup): boolean {
    if (!blockGroup.reviewRequest?.get('reviewReason')) {
      return false;
    }
    return (
      (blockGroup.isEdited && blockGroup.reviewRequest?.get('status') === Status.Pending) ||
      blockGroup.reviewRequest?.get('reviewType') === ReviewType.CREATE_CONTENT
    );
  }

  @action
  scrollToAndFocusOnSuggestion(blockGroup: BlockGroup, shouldScroll = true) {
    if (shouldScroll) {
      this.scrollToBlockGroup(blockGroup);
    }
    this.focusOnBlockGroupSuggestion(blockGroup);
  }

  @action
  focusOnBlockGroupSuggestion(blockGroup?: BlockGroup) {
    let blockGroupToFocus = blockGroup ?? this.editedBlockGroups[0];

    if (!blockGroupToFocus?.isEdited) {
      this.focusedBlockGroup = undefined;
      return;
    }

    this.focusedBlockGroup = blockGroupToFocus;
    this.positionSuggestionCardsWithoutOverlap(blockGroupToFocus);
  }

  @action
  isNextSuggestionButtonDisabled(blockGroup: BlockGroup) {
    return (
      this.editedBlockGroupCardNumber(blockGroup) ===
      this.suggestionService.numberOfPendingSuggestions
    );
  }

  @action
  isPreviousSuggestionButtonDisabled(blockGroup: BlockGroup) {
    return this.editedBlockGroupCardNumber(blockGroup) === 1;
  }

  @action
  onNextSuggestion(suggestionNumber: number) {
    this.scrollToAndFocusOnSuggestion(this.pendingEditedBlockGroups[suggestionNumber]);
  }

  @action
  onPreviousSuggestion(suggestionNumber: number) {
    this.scrollToAndFocusOnSuggestion(this.pendingEditedBlockGroups[suggestionNumber - 2]);
  }

  @action
  focusOnSuggestion(blockGroup: BlockGroup) {
    this.scrollToAndFocusOnSuggestion(blockGroup, false);
  }

  @action
  isFocusedOnBlockGroup(blockGroup: BlockGroup) {
    return (
      blockGroup.id === this.focusedBlockGroup?.id ||
      blockGroup.reviewRequest?.get('reviewType') === ReviewType.CREATE_CONTENT ||
      this.suggestionService.numberOfPendingSuggestions === 1
    );
  }

  @action
  editedBlockGroupCardNumber(blockGroup: BlockGroup) {
    return this.pendingEditedBlockGroups.findIndex((group) => group.id === blockGroup.id) + 1;
  }

  @action
  getJsonBlocks(activeContent: ReviewableContent): Block[] {
    return activeContent.entityType === EntityType.ContentSnippet
      ? activeContent.serializedBlocks
      : activeContent.jsonBlocks;
  }

  get reviewRequestToReplace() {
    // All pending review requests have the same seed content, so we can just use the first one
    return this.pendingEditedBlockGroups[0].reviewRequest;
  }

  @action
  closeModal() {
    this.moveContentModalOpen = false;
  }

  @action
  showMoveContentModal() {
    this.moveContentModalOpen = true;
  }

  @action
  onMoveRequested(entity_id: string | number, entity_type: string) {
    this.moveContentModalOpen = false;
    this.knowledgeHubDrawerEditorService.openReviewDrawer({
      activeContentId: entity_id,
      activeContentType: entity_type,
    });
  }

  @action
  async onSuggestionCardAction() {
    // Wait for DOM to update
    await timeout(ENV.APP._200MS);
    this.positionSuggestionCardsWithoutOverlap();
  }

  /**
   * Position suggestion cards so that each is aligned with its block group, but if any would overlap,
   * move them down to avoid overlap, cascading as needed.
   */
  private positionSuggestionCardsWithoutOverlap(focusedBlockGroup?: BlockGroup) {
    let container = document.getElementById('suggestion-editor-content');
    if (!container) {
      return;
    }
    let containerRect = container.getBoundingClientRect();
    let cards = this.gatherCardDetails(containerRect);

    if (focusedBlockGroup) {
      this.applyCardPositionsWithFocusedBlockGroup(focusedBlockGroup, cards);
    } else {
      // No focused card, cascade all from the top
      let lastCardBottom = 0;
      for (let i = 0; i < cards.length; i++) {
        let { idealTop, cardElement, cardHeight } = cards[i];
        let top = i === 0 ? idealTop : Math.max(idealTop, lastCardBottom + SPACING_BETWEEN_CARDS);
        cardElement.style.top = `${top}px`;
        lastCardBottom = top + cardHeight;
      }
    }
  }

  private applyCardPositionsWithFocusedBlockGroup(
    focusedBlockGroup: BlockGroup,
    cards: SuggestionCardLayoutDetails[],
  ) {
    // Find the focused card index
    let focusedCardIndex = cards.findIndex((c) => c.blockGroupId === focusedBlockGroup.id);
    if (focusedCardIndex === -1) {
      return;
    }
    // Pin the focused card
    let focusedCard = cards[focusedCardIndex];
    focusedCard.cardElement.style.top = `${focusedCard.idealTop}px`;
    // Cascade cards above
    let prevBottom = focusedCard.idealTop;
    for (let i = focusedCardIndex - 1; i >= 0; i--) {
      let card = cards[i];
      // Place as close as possible to its ideal, but not overlapping the card below
      let maxBottom = prevBottom - SPACING_BETWEEN_CARDS;
      let top = Math.min(card.idealTop, maxBottom - card.cardHeight);
      card.cardElement.style.top = `${top}px`;
      prevBottom = top;
    }
    // Cascade cards below
    let lastCardBottom = focusedCard.idealTop + focusedCard.cardHeight;
    for (let i = focusedCardIndex + 1; i < cards.length; i++) {
      let card = cards[i];
      // Place as close as possible to its ideal, but not overlapping the card above
      let minTop = lastCardBottom + SPACING_BETWEEN_CARDS;
      let top = Math.max(card.idealTop, minTop);
      card.cardElement.style.top = `${top}px`;
      lastCardBottom = top + card.cardHeight;
    }
  }

  private gatherCardDetails(containerRect: DOMRect) {
    // Gather all cards and their block group positions
    let cards: SuggestionCardLayoutDetails[] = [];
    for (let i = 0; i < this.editedBlockGroups.length; i++) {
      let blockGroup = this.editedBlockGroups[i];
      let cardId = `suggestion-card-${this.editedBlockGroupCardNumber(blockGroup)}`;
      let blockGroupElement = document.getElementById(blockGroup.id);
      let cardElement = document.getElementById(cardId);
      if (blockGroupElement && cardElement) {
        let blockGroupRect = blockGroupElement.getBoundingClientRect();
        let idealTop = blockGroupRect.top - containerRect.top;
        let cardHeight = cardElement.offsetHeight;
        cards.push({ blockGroupId: blockGroup.id, cardId, idealTop, cardElement, cardHeight });
      }
    }
    // Sort by idealTop (top to bottom)
    return cards.sort((a, b) => a.idealTop - b.idealTop);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::Shared::SuggestionEditor': typeof SuggestionEditor;
  }
}
